import useDownloadOrderSheetPdf, { OrderSheetKind } from "hooks/api/useDownloadOrderSheetPdf";
import useDownloadServiceEstimatePdf from "hooks/api/useDownloadServiceEstimatePdf";
import useFindContractPdf from "hooks/api/useFindContractPdf";
import useFindEstimateAttachment from "hooks/api/useFindEstimateAttachment";
import useFindParkingGuideByContractId from "hooks/api/useFindParkingGuideByContractId";
import useFindParkingGuideByEstimateId from "hooks/api/useFindParkingGuideByEstimateId";
import useErrorModalStatusContext from "hooks/useErrorModalStatusContext";
import { useGoogleAnalytics } from "hooks/useGoogleAnalytics";
import useUserContext from "hooks/useUserContext";
import { downloadPdf } from "utils/fileUtils";

// 本体
const usePdfDownloadProvider = () => {
  const { sendEvent } = useGoogleAnalytics(true);

  const { findEstimateAttachment } = useFindEstimateAttachment();

  const { download: downloadServiceEstimatePdf } = useDownloadServiceEstimatePdf();

  const { download: downloadOrderSheetPdf } = useDownloadOrderSheetPdf();

  const { findContractPdf } = useFindContractPdf();

  const { showErrorModal } = useErrorModalStatusContext();

  const { user } = useUserContext();

  const { findParkingGuide: findParkingGuideByEstimateId } = useFindParkingGuideByEstimateId();
  const { findParkingGuide: findParkingGuideByContractId } = useFindParkingGuideByContractId();

  const downloadEstimatePdf = async (estimateId: string, constructionName: string): Promise<void> => {
    const attachment = await findEstimateAttachment(estimateId).catch(() => showErrorModal({ httpMethod: "get" }));
    attachment
      ? downloadPdf(attachment, `${user.companyName}御中_${constructionName}_見積書`)
      : showErrorModal({ httpMethod: "get" });
    sendEvent({
      category: "見積書をダウンロード",
      action: "link",
      label: "/estimate",
    });
  };

  const downloadContractPdf = async (contractId: string, constructionName: string): Promise<void> => {
    const attachment = await findContractPdf(contractId).catch(() => showErrorModal({ httpMethod: "get" }));
    attachment
      ? downloadPdf(attachment, `${user.companyName}御中_${constructionName}_ご契約書`)
      : showErrorModal({ httpMethod: "get" });
    sendEvent({
      category: "契約書ダウンロード",
      action: "link",
      label: "/contract",
    });
  };

  const downloadParkingGuidePdfByEstimateId = async (estimateId: string, constructionName: string): Promise<void> => {
    const attachment = await findParkingGuideByEstimateId(estimateId).catch(() =>
      showErrorModal({ httpMethod: "get" })
    );
    attachment
      ? downloadPdf(attachment, `${user.companyName}御中_${constructionName}_区画図`)
      : showErrorModal({ httpMethod: "get" });
    sendEvent({
      category: "区画図をダウンロード",
      action: "link",
      label: "/estimate",
    });
  };

  const downloadParkingGuidePdfByContractId = async (estimateId: string, constructionName: string): Promise<void> => {
    const attachment = await findParkingGuideByContractId(estimateId).catch(() =>
      showErrorModal({ httpMethod: "get" })
    );
    attachment
      ? downloadPdf(attachment, `${user.companyName}御中_${constructionName}_区画図`)
      : showErrorModal({ httpMethod: "get" });
    sendEvent({
      category: "区画図をダウンロード",
      action: "link",
      label: "/estimate",
    });
  };

  const downloadServiceEstimatePdfByEstimateId = async (
    optionId: string,
    serviceId: string,
    estimateId: string
  ): Promise<void> => {
    const attachment = await downloadServiceEstimatePdf(optionId, serviceId, estimateId).catch(() =>
      showErrorModal({ httpMethod: "get" })
    );
    attachment
      ? downloadPdf(attachment, `${user.companyName}御中_サービス見積書_${optionId}_${serviceId}`)
      : showErrorModal({ httpMethod: "get" });
    sendEvent({
      category: "サービス見積書をダウンロード",
      action: "link",
      label: `/option/${optionId}/request-service/${serviceId}/estimate/${estimateId}`,
    });
  };

  // 注文書は見積書などと同じく、リンク遷移ではなくその場でファイルを保存する。
  // 取得に失敗したら false を返し、呼び出し側が同意チェックを開けないようにする。
  const downloadOrderSheetPdfByEstimateId = async (
    optionId: string,
    serviceId: string,
    estimateId: string,
    kind: OrderSheetKind,
    constructionName: string
  ): Promise<boolean> => {
    const attachment = await downloadOrderSheetPdf(optionId, serviceId, estimateId, kind).catch(() =>
      showErrorModal({ httpMethod: "get" })
    );
    // 交付書面なので、Salesforce に記録されている名前のまま保存する。
    // 命名は Salesforce 側の1箇所で決まり、発注前後の区別もそこに含まれる。
    const downloaded =
      attachment && "filename" in attachment
        ? downloadPdf(attachment, attachment.filename.replace(/\.pdf$/i, ""))
        : false;

    if (!downloaded) {
      showErrorModal({ httpMethod: "get" });
      return false;
    }

    sendEvent({
      category: kind === "draft" ? "発注前の注文書をダウンロード" : "注文書をダウンロード",
      action: "link",
      label: `/option/${optionId}/request-service/${serviceId}/estimate/${estimateId}`,
    });
    return true;
  };

  return {
    downloadEstimatePdf,
    downloadOrderSheetPdfByEstimateId,
    downloadParkingGuidePdfByEstimateId,
    downloadParkingGuidePdfByContractId,
    downloadContractPdf,
    downloadServiceEstimatePdfByEstimateId,
  };
};

export default usePdfDownloadProvider;
